/**
 * custom-fit-covers.js
 * Oceansouth — Custom Fit Finder
 *
 * ✔ Shopify Markets-native
 * ✔ No custom market filtering
 * ✔ No availability filtering
 * ✔ Multi-instance safe
 * ✔ Defer-safe
 * ✔ Session caching
 */

(() => {
  "use strict";

  const SELECTOR = ".custom-fit-covers[id^='custom-fit-']";
  const CACHE_TTL_MS = 10 * 60 * 1000;

  function safeUpper(value) {
    return String(value || "").trim().toUpperCase();
  }

  function ensureTrailingSlash(path) {
    const s = String(path || "/").trim();
    return s.endsWith("/") ? s : s + "/";
  }

  function getApiEndpoint(rootEl) {
    const version = rootEl?.dataset?.apiVersion || "2024-10";
    return `/api/${version}/graphql.json`;
  }

  function cacheKey(rootEl) {
    const country = safeUpper(rootEl.dataset.country);
    const token = rootEl.dataset.token || "";
    return `os_custom_fit_v2:${country}:${token.slice(0, 8)}`;
  }

  function readCache(rootEl) {
    try {
      const key = cacheKey(rootEl);
      const raw = sessionStorage.getItem(key);
      if (!raw) return null;

      const parsed = JSON.parse(raw);
      if (!parsed || !parsed.ts || !parsed.data) return null;

      if (Date.now() - parsed.ts > CACHE_TTL_MS) {
        sessionStorage.removeItem(key);
        return null;
      }

      return parsed.data;
    } catch {
      return null;
    }
  }

  function writeCache(rootEl, data) {
    try {
      const key = cacheKey(rootEl);
      sessionStorage.setItem(key, JSON.stringify({ ts: Date.now(), data }));
    } catch {}
  }

  async function storefrontGraphQL({ endpoint, token, query }) {
    const res = await fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Shopify-Storefront-Access-Token": token,
      },
      body: JSON.stringify({ query }),
      credentials: "same-origin",
    });

    const json = await res.json();

    if (json?.errors?.length) {
      throw new Error(json.errors.map(e => e.message).join(" | "));
    }

    return json;
  }

  async function fetchAllCustomFits(rootEl, country) {
    const token = rootEl.dataset.token || "";
    const endpoint = getApiEndpoint(rootEl);

    const cached = readCache(rootEl);
    if (cached) return cached;

    const allEdges = [];
    let cursor = null;
    let hasNext = true;

    while (hasNext) {
      const query = `
        query @inContext(country: ${country}) {
          metaobjects(type: "custom_fit", first: 250${cursor ? `, after: "${cursor}"` : ""}) {
            edges {
              cursor
              node {
                fields {
                  key
                  value
                  reference {
                    ... on Product {
                      handle
                    }
                  }
                }
              }
            }
            pageInfo { hasNextPage endCursor }
          }
        }
      `;

      const json = await storefrontGraphQL({ endpoint, token, query });
      const page = json?.data?.metaobjects;
      if (!page) break;

      allEdges.push(...(page.edges || []));
      hasNext = Boolean(page.pageInfo?.hasNextPage);
      cursor = page.pageInfo?.endCursor || null;
    }

    const fits = allEdges.map(edge => {
      const obj = {};
      const fields = edge?.node?.fields || [];

      for (const f of fields) {
        const key = f.key;

        if (f.reference) {
          obj[key] = f.reference.handle || "";
        } else {
          obj[key] = f.value || "";
        }
      }

      return obj;
    });

    writeCache(rootEl, fits);
    return fits;
  }

  function buildCatalogue(fits) {
    const catalogue = {};

    for (const fit of fits) {
      const vendor = String(fit.vendor_name || "").trim();
      const title = String(fit.model_name || fit.title || "").trim();
      const handle = String(fit.model_url_link || "").trim();

      if (!vendor || !title || !handle) continue;

      (catalogue[vendor] ||= []).push({ title, handle });
    }

    for (const vendor of Object.keys(catalogue)) {
      catalogue[vendor].sort((a, b) =>
        a.title.localeCompare(b.title)
      );
    }

    return catalogue;
  }

  function initUI(rootEl, catalogue) {
    const blockId = rootEl.id.replace("custom-fit-", "");
    const vendorSelect = document.getElementById(`vendor-select-${blockId}`);
    const modelSelect = document.getElementById(`model-select-${blockId}`);
    const findBtn = document.getElementById(`find-cover-${blockId}`);

    if (!vendorSelect || !modelSelect || !findBtn) return;

    vendorSelect.disabled = false;
    modelSelect.disabled = true;
    findBtn.disabled = true;
    vendorSelect.length = 1;

    Object.keys(catalogue)
      .sort((a, b) => a.localeCompare(b))
      .forEach(vendor => {
        const opt = document.createElement("option");
        opt.value = vendor;
        opt.textContent = vendor;
        vendorSelect.appendChild(opt);
      });

    function resetModels() {
      modelSelect.innerHTML = '<option value="">Select Model</option>';
      modelSelect.disabled = true;
      findBtn.disabled = true;
    }

    vendorSelect.addEventListener("change", function () {
      const vendor = this.value;
      resetModels();

      if (!vendor || !catalogue[vendor]) return;

      modelSelect.disabled = false;

      catalogue[vendor].forEach(item => {
        const opt = document.createElement("option");
        opt.value = item.handle;
        opt.textContent = item.title;
        modelSelect.appendChild(opt);
      });
    });

    modelSelect.addEventListener("change", function () {
      findBtn.disabled = !this.value;
    });

    findBtn.addEventListener("click", function () {
      const handle = modelSelect.value;
      if (!handle) return;

      const rootUrl = ensureTrailingSlash(rootEl.dataset.rootUrl || "/");
      window.location.assign(`${rootUrl}products/${handle}`);
    });
  }

  async function initOne(rootEl) {
    if (!rootEl || rootEl.dataset.initialised === "1") return;
    rootEl.dataset.initialised = "1";

    const token = rootEl.dataset.token || "";
    const country = safeUpper(rootEl.dataset.country);

    if (!token || !country || country.length !== 2) return;

    try {
      const fits = await fetchAllCustomFits(rootEl, country);
      const catalogue = buildCatalogue(fits);
      initUI(rootEl, catalogue);
    } catch (err) {
      console.error("[CustomFitFinder] Init failed:", err);
    }
  }

  function initAll() {
    document.querySelectorAll(SELECTOR).forEach(el => initOne(el));
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAll);
  } else {
    initAll();
  }

})();